import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { QuickAccessButton } from '../QuickAccessButton/QuickAccessButton';

interface GoToTopButtonProps {
  containerRef:
    | MutableRefObject<HTMLElement | HTMLDivElement | null>
    | undefined;
}

export const GoToTopButton = ({ containerRef }: GoToTopButtonProps) => {
  const [scrollTop, setScrollTop] = useState<number>(0);

  const getScrollableContainer = useCallback(() => {
    const container = containerRef?.current;

    if (container && container.scrollHeight > container.clientHeight) {
      return container;
    }

    return null;
  }, [containerRef]);

  const getCurrentScrollTop = useCallback(() => {
    const container = getScrollableContainer();

    if (container) return container.scrollTop;

    return window.scrollY || document.scrollingElement?.scrollTop || 0;
  }, [getScrollableContainer]);

  const setCurrentScrollTop = useCallback(
    (top: number) => {
      const container = getScrollableContainer();

      if (container) {
        container.scrollTop = top;
        return;
      }

      window.scrollTo(0, top);
    },
    [getScrollableContainer]
  );

  const goUp = () => {
    const start = getCurrentScrollTop();

    if (start <= 0) {
      setCurrentScrollTop(0);
      return;
    }

    const duration = Math.min(700, Math.max(320, start * 0.45));
    const startTime = performance.now();
    const easeOutCubic = (value: number) => 1 - (1 - value) ** 3;

    const animate = (time: number) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const nextTop = start * (1 - easeOutCubic(progress));

      setCurrentScrollTop(nextTop);

      if (progress < 1) {
        window.requestAnimationFrame(animate);
      }
    };

    window.requestAnimationFrame(animate);
  };

  useEffect(() => {
    const ref = containerRef?.current;
    const updateScrollTop = () => {
      setScrollTop(getCurrentScrollTop());
    };

    updateScrollTop();
    ref?.addEventListener('scroll', updateScrollTop, { passive: true });
    window.addEventListener('scroll', updateScrollTop, { passive: true });

    return () => {
      ref?.removeEventListener('scroll', updateScrollTop);
      window.removeEventListener('scroll', updateScrollTop);
    };
  }, [containerRef, getCurrentScrollTop]);

  return (
    <QuickAccessButton
      icon="arrow-up"
      onClick={goUp}
      isVisible={scrollTop > 500}
      ariaLabel="Scroll to top"
    />
  );
};
